import React from 'react';
import dayjs from 'dayjs';
import { ListFilter } from 'lucide-react';

import { Grid, Form, DateInput, Button, Tooltip } from '@mairistem/composants';

import { ModalRegistreTreeView } from '@/components/Registre';
import { Section } from '@/components/Common';
import {
  STATUT_PACS,
  MOTIFS_IRRECEVABILITE,
  OPTIONS_TYPE_COMPARUTION,
} from '@/constants';
import type { Registre } from '@/types';
import { useRegistreStore } from '@/zustand';
import { AssistantContext } from '@/contexts';
import { computeNextPacsNumber } from '@/utils';

interface InformationsPacsProps {
  numero: number;
  statut: number;
  registre: Partial<Registre> | null;
  dateIrrecevabilite: string;
  dateEnregistrement: string;
  identifiantIrrecevabilite: number;
  typeComparution: number;
  errors: Record<string, any>;
  onChange: (
    _e: any,
    data: { name: string; value?: any; checked?: boolean },
  ) => void;
}

const InformationsPACS = (props: InformationsPacsProps) => {
  const {
    numero,
    statut,
    registre,
    dateIrrecevabilite,
    dateEnregistrement,
    identifiantIrrecevabilite,
    typeComparution,
    errors,
    onChange,
  } = props;

  const setRegistre = useRegistreStore((state) => state.setRegistre);

  const [openModal, setOpenModal] = React.useState(false);

  // statut = 2 : irrecevable : on cache les champs
  const [showIrrecevabiliteFields, setShowIrrecevabiliteFields] =
    React.useState(statut === 2);

  const { selectedDeclarationType, isNumeroLocked, resetPacsNumero } =
    React.useContext(AssistantContext);

  const isProjet = selectedDeclarationType === 'projet';

  React.useEffect(() => {
    setShowIrrecevabiliteFields(statut === 2);
  }, [statut]);

  const handleSelectRegistre = React.useCallback(
    (value: Partial<Registre>) => {
      const nextPacsNumber = computeNextPacsNumber(value as any);

      onChange(null, {
        name: 'registre',
        value,
      });

      if (nextPacsNumber && !isNumeroLocked) {
        onChange(null, {
          name: 'numero',
          value: nextPacsNumber,
        });
      }

      setRegistre(value as Registre);
      setOpenModal(false);
    },
    [onChange, isNumeroLocked, setRegistre],
  );

  const handlePacsFieldChange = React.useCallback(
    (name: string, value: string | number) => {
      onChange(null, {
        name,
        value,
      });
    },
    [onChange],
  );

  const handleConventionFieldChange = React.useCallback(
    (field: string, value: string | number | null) => {
      onChange(null, {
        name: `conventionDeclaration.${field}`,
        value,
      });
    },
    [onChange],
  );

  const handleTypeComparutionChange = React.useCallback(
    (_e: React.SyntheticEvent | null, data: { value?: string | number }) => {
      if (
        data?.value === null ||
        data?.value === undefined ||
        data?.value === ''
      ) {
        return;
      }

      const nextValue = Number(data.value);

      if (Number.isNaN(nextValue)) {
        return;
      }

      handleConventionFieldChange('typeComparution', nextValue);
    },
    [handleConventionFieldChange],
  );

  const handleDateChange = React.useCallback(
    (
      field: 'dateEnregistrement' | 'dateIrrecevabilite',
      rawValue: string | Date | null | undefined,
    ) => {
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        handleConventionFieldChange(field, null);
        return;
      }

      const parsedDate =
        rawValue instanceof Date ? rawValue : new Date(rawValue);
      const nextValue = Number.isNaN(parsedDate.getTime())
        ? null
        : dayjs(parsedDate).format('YYYY-MM-DD');

      handleConventionFieldChange(field, nextValue);
    },
    [handleConventionFieldChange],
  );

  return (
    <>
      <Section
        data-testid="assistant-informations-pacs"
        title="INFORMATIONS DU PACS"
      >
        <Form>
          <Grid padded>
            <Grid.Row>
              <Grid.Column width={4}>
                <Tooltip
                  trigger={
                    <Form.Input
                      required
                      label="Pacs enregistré sur le registre"
                      input={{
                        'data-testid': 'assistant-informations-pacs-registre',
                      }}
                      value={
                        registre
                          ? `Année ${registre?.annee?.annee} - Registre de ${registre?.libelle}`
                          : ''
                      }
                      action={
                        <Button
                          type="button"
                          basic
                          data-testid="registre-input-bouton-plus"
                          icon={<ListFilter size={16} />}
                          onClick={() => setOpenModal(true)}
                        />
                      }
                      message="Chercher un registre"
                    />
                  }
                  type={errors?.registre ? 'error' : ''}
                  message={errors?.registre}
                />
              </Grid.Column>
              <Grid.Column width={2}>
                <Form.Input required={!isProjet} label="le">
                  <Tooltip
                    trigger={
                      <div>
                        <DateInput
                          data-testid="assistant-informations-pacs-date-enregistrement"
                          value={
                            dateEnregistrement
                              ? new Date(dateEnregistrement)
                              : undefined
                          }
                          onChange={(value) =>
                            handleDateChange('dateEnregistrement', value)
                          }
                          error={!!errors?.dateEnregistrement}
                        />
                      </div>
                    }
                    type={errors?.dateEnregistrement ? 'error' : ''}
                    message={errors?.dateEnregistrement}
                  />
                </Form.Input>
              </Grid.Column>
              <Grid.Column width={3}>
                <Tooltip
                  trigger={
                    <div>
                      <Form.Input
                        className={errors?.numero ? 'field-error-wrapper' : ''}
                        error={!!errors?.numero}
                        required
                        label="de numéro"
                        input={{
                          'data-testid': 'assistant-informations-pacs-numero',
                        }}
                        value={numero ?? ''}
                        onChange={(_e, data) =>
                          handlePacsFieldChange(
                            'numero',
                            data?.value as string | number,
                          )
                        }
                        disabled={
                          registre?.numerotationManuelle === 0 || isNumeroLocked
                        }
                        action={
                          <Button
                            type="button"
                            error
                            basic
                            data-testid="numero-input-bouton-reset"
                            icon="sync"
                            onClick={() => resetPacsNumero()}
                            disabled={
                              registre?.numerotationManuelle === 0 ||
                              isNumeroLocked
                            }
                          />
                        }
                      />
                    </div>
                  }
                  type={errors?.numero ? 'error' : ''}
                  message={errors?.numero}
                />
              </Grid.Column>
              <Grid.Column width={2}>
                <Form.Dropdown
                  selection
                  label="Statut"
                  data-testid="assistant-informations-pacs-statut"
                  options={STATUT_PACS}
                  value={statut ?? STATUT_PACS[0]?.value}
                  onChange={(_e, data) =>
                    handlePacsFieldChange(
                      'statut',
                      data.value as string | number,
                    )
                  }
                  disabled={isProjet}
                />
              </Grid.Column>
              <Grid.Column width={5}>
                <Form.Dropdown
                  selection
                  label="Type de comparution des partenaires"
                  data-testid="assistant-informations-pacs-type-comparution"
                  options={OPTIONS_TYPE_COMPARUTION}
                  value={typeComparution ?? OPTIONS_TYPE_COMPARUTION[0]?.value}
                  onChange={handleTypeComparutionChange}
                />
              </Grid.Column>
            </Grid.Row>

            {showIrrecevabiliteFields && (
              <Grid.Row style={{ paddingTop: 0 }}>
                <Grid.Column width={2}>
                  <Tooltip
                    trigger={
                      <Form.Input label="Date d'irrecevabilité">
                        <DateInput
                          data-testid="assistant-informations-pacs-date-irrecevabilite"
                          value={
                            dateIrrecevabilite
                              ? new Date(dateIrrecevabilite)
                              : undefined
                          }
                          onChange={(value) =>
                            handleDateChange('dateIrrecevabilite', value)
                          }
                        />
                      </Form.Input>
                    }
                    type={errors?.dateIrrecevabilite ? 'error' : ''}
                    message={errors?.dateIrrecevabilite}
                  />
                </Grid.Column>
                <Grid.Column width={6}>
                  <Form.Dropdown
                    selection
                    label="Motif"
                    data-testid="assistant-informations-pacs-motif-irrecevabilite"
                    options={MOTIFS_IRRECEVABILITE}
                    value={identifiantIrrecevabilite}
                    onChange={(_e, data) =>
                      handleConventionFieldChange(
                        'identifiantIrrecevabilite',
                        data.value as number,
                      )
                    }
                  />
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </Form>
      </Section>
      <ModalRegistreTreeView
        open={openModal}
        isProjet={isProjet}
        onClose={() => setOpenModal(false)}
        onSelect={handleSelectRegistre}
      />
    </>
  );
};

export default InformationsPACS;
